import { Form, Button, theme, Typography, List, Col, Row } from "antd";
import { useEffect, useState } from "react";
import {
  addTestGroup,
  deleteTestGroup,
  updateTestGroup,
  getTestGroupUsers,
  addTestGroupUser,
  deleteTestGroupUser,
  getCompanyEmployees,
} from "../../repository/data.repository";
import { TestGroupsForm } from "./TestGroupsFormDialog.default";
import DynamicForm from "../../controls/DynamicForm";
import { QueryOperation } from "../../code/search-query/search-query";
import { nameOf } from "../../utils/typescript";
import { retry, getProblemMessage } from "../../utils/http";
import InModal from "../../controls/InModal";
import { modalTitle } from "../../controls/InModal.default";
import ModalFooter from "../../controls/ModalFooter";
import ModalDeleteConfirmation from "../../controls/ModalDeleteConfirmation";
import ModalErrorAlert from "../../controls/ModalErrorAlert";
import { Icons } from "../../assets/icons";
import Icon from "@ant-design/icons";
import { TestGroup, EmployeeUser } from "../../repository/data.default";
import _ from "lodash";
import { InputObject } from "../../controls/default";
import { companiesRequest, bundlesRequest } from "../bundles/bundleFilters";

export interface TestGroupsFormDialogProps {
  readonly open?: boolean;
  readonly mode: "add" | "update" | "delete";
  readonly initialValues?: TestGroup;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
}

function TestGroupsFormDialog(props: TestGroupsFormDialogProps) {
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const values = Form.useWatch<TestGroupsForm>([], form);
  const [valid, setValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  function onError(e: unknown): void {
    setLoading(false);
    setErrorMessage(getProblemMessage(e, "Something went wrong, try again"));
  }
  // Users Form & List
  const [usersForm] = Form.useForm();
  const usersValues = Form.useWatch<{
    readonly user: InputObject;
  }>([], usersForm);
  const [users, setUsers] = useState<InputObject[]>([]);
  const [dataLoading, setDataLoading] = useState(false);

  //Lifecycle

  useEffect(() => {
    if (props.open) {
      if (props.initialValues?.uid) {
        setDataLoading(true);
        retry(() => getTestGroupUsers(props.initialValues?.uid!)).then(
          (userData) => {
            setDataLoading(false);
            form.setFieldsValue(props.initialValues);
            usersForm.resetFields();
            const users = userData.data.map((item) => ({
              display: item.userUserName,
              value: item.userUid,
              dataItem: { ...item, __type: "update" },
            }));
            setUsers(users);
          },
        );
      } else {
        usersForm.resetFields();
        form.setFieldsValue(props.initialValues);
        setUsers([]);
      }
    } else {
      reset();
    }
  }, [props.open]);

  useEffect(() => {
    form
      .validateFields({
        validateOnly: true,
      })
      .then((value) => {
        setValid(!!Object.values(value).length);
      })
      .catch(() => {
        setValid(false);
      });
  }, [form, values, props.open]);

  //Events

  function onSubmit(): void {
    const { company, bundle, allowedSubmissions } = values;
    setLoading(true);
    setErrorMessage(null);
    if (props.mode === "add") {
      addTestGroup({
        bundleUid: bundle?.value,
        companyUid: company?.value,
        allowedSubmissions,
      })
        .then(({ uid }) =>
          addOrDeleteTestGroupUsers(uid).then(() => {
            setLoading(false);
            props.onFinish?.();
          }),
        )
        .catch(onError);
    } else if (props.mode === "update") {
      updateTestGroup({
        uid: props.initialValues?.uid,
        allowedSubmissions,
        rowVersion: props.initialValues?.rowVersion,
      })
        .then(() =>
          addOrDeleteTestGroupUsers(props.initialValues?.uid!).then(() => {
            setLoading(false);
            props.onFinish?.();
          }),
        )
        .catch(onError);
    } else if (props.mode === "delete") {
      deleteTestGroup(props.initialValues?.uid!)
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(onError);
    }
  }

  function onCancel(): void {
    props.onCancel?.();
  }

  function onAddUser(): void {
    const { user } = usersValues || {};
    if (user != null) {
      const updateUsers = _.uniqBy(
        [
          ...users.filter((item) => item.dataItem.uid != user.dataItem.uid),
          { ...user, dataItem: { ...user.dataItem, __type: "add" } },
        ],
        (item) => item.value,
      );
      setUsers(updateUsers);
      usersForm.resetFields();
    }
  }

  function onRemoveUser(uid: string): void {
    const filterUser = users.map((item, i) => {
      if (item.dataItem.uid == uid) {
        return { ...item, dataItem: { ...item.dataItem, __type: "delete" } };
      }

      return item;
    });
    setUsers(filterUser);
  }

  // Methods

  function reset(): void {
    setLoading(false);
    setValid(false);
    setErrorMessage(null);
    form.resetFields();
    usersForm.resetFields();
  }

  function addOrDeleteTestGroupUsers(TestGroupUid: string) {
    const addUsers = users.filter(({ dataItem }) => dataItem.__type == "add");
    const deleteUsers = users.filter(
      ({ dataItem }) => dataItem.__type == "delete",
    );

    const promises: Promise<void>[] = [];

    if (deleteUsers.length) {
      deleteUsers.forEach(({ value }) => {
        promises.push(deleteTestGroupUser(TestGroupUid, value));
      });
    }

    if (addUsers.length) {
      addUsers.forEach(({ value }) => {
        promises.push(addTestGroupUser(TestGroupUid, value));
      });
    }

    return Promise.all(promises);
  }

  const companyEmployeesRequest = (search: string) => {
    const searchQuery = {
      conditions: [
        {
          field: nameOf<EmployeeUser>("firstName"),
          operation: QueryOperation.StartsWith,
          values: [search],
        },
      ],
    };

    if (values?.company?.value) {
      searchQuery.conditions.push({
        field: nameOf<EmployeeUser>("companyUid"),
        operation: QueryOperation.Equal,
        values: [form.getFieldValue("company")?.value],
      });
    }
    return retry(() =>
      getCompanyEmployees({
        searchQuery,
        pageNumber: 0,
        pageSize: 100,
      }),
    ).then(({ data }) =>
      data.map((dataItem) => ({
        display: dataItem.userName,
        subDisplay: dataItem.companyName,
        value: dataItem.uid,
        dataItem: dataItem,
      })),
    );
  };

  return (
    <InModal
      open={props.open}
      loading={dataLoading}
      loadingLabel="Loading test group…"
      size={props.mode === "delete" ? "small" : "large"}
      title={modalTitle(props.mode, "Group", undefined)}
      onCancel={onCancel}
      actions={
        <ModalFooter
          mode={props.mode}
          loading={loading}
          valid={valid}
          onCancel={onCancel}
          onSubmit={onSubmit}
        />
      }
    >
      {props.mode !== "delete" && (
        <Row className="mb-4 flex flex-row">
          {/* Test Group Form */}
          <Col className="flex flex-1 flex-col">
            <DynamicForm
              className="w-auto"
              form={form}
              schema={{
                groups: [
                  {
                    controls: [
                      {
                        type: "combobox",
                        name: "company",
                        label: "Company",
                        disabled: props.mode === "add" ? false : true,
                        rules: [
                          {
                            required: true,
                            message: "Company is required",
                          },
                        ],
                        request: companiesRequest,
                      },
                      {
                        type: "combobox",
                        name: "bundle",
                        label: "Bundle",
                        disabled: props.mode === "add" ? false : true,
                        rules: [
                          {
                            required: true,
                            message: "Bundle is required",
                          },
                        ],
                        request: bundlesRequest,
                      },
                      {
                        type: "numberbox",
                        name: "allowedSubmissions",
                        label: "Allowed submissions",
                        step: "1",
                        rules: [
                          {
                            required: true,
                            message: "Allowed submissions is required",
                          },
                          {
                            validator: (_, value) => {
                              const numValue = Number(value);
                              if (!Number.isInteger(numValue)) {
                                return Promise.reject(
                                  new Error("Only integer values are allowed"),
                                );
                              } else {
                                return Promise.resolve();
                              }
                            },
                          },
                        ],
                      },
                    ],
                  },
                ],
              }}
            />
          </Col>
          {/* Users Form & List */}
          <Col className="flex flex-1 flex-col">
            <DynamicForm
              className="w-full"
              form={usersForm}
              schema={{
                groups: [
                  {
                    controls: [
                      {
                        type: "combobox",
                        label: "Users",
                        name: "user",
                        disabled: !values?.company?.value,
                        request: companyEmployeesRequest,
                      },
                    ],
                  },
                ],
              }}
              onChange={onAddUser}
            />

            {!!users.filter(({ dataItem }) => dataItem.__type != "delete")
              .length &&
              (props.mode === "add" || props.mode === "update") && (
                <List
                  className="ml-1 mr-1"
                  style={{
                    backgroundColor: token.colorBgLayout,
                    borderRadius: token.borderRadius,
                  }}
                  dataSource={users.filter(
                    ({ dataItem }) => dataItem.__type != "delete",
                  )}
                  renderItem={(item, index) => {
                    return (
                      <List.Item className="p-1 pl-2 pr-2">
                        <Col className="flex flex-auto flex-col">
                          <Typography.Text>{item.display}</Typography.Text>
                          <Typography.Text
                            style={{
                              color: token.colorTextDescription,
                            }}
                          >
                            {item.subDisplay}
                          </Typography.Text>
                        </Col>
                        <Button
                          type={"text"}
                          icon={
                            <Icon
                              className="-ml-0.5 text-xl"
                              component={Icons.CloseIcon}
                            />
                          }
                          onClick={() => onRemoveUser(item.dataItem.uid)}
                        />
                      </List.Item>
                    );
                  }}
                />
              )}
          </Col>
        </Row>
      )}

      {props.mode === "delete" && (
        <ModalDeleteConfirmation entityLabel="test group" />
      )}
      <ModalErrorAlert message={errorMessage} />
    </InModal>
  );
}

export default TestGroupsFormDialog;
